import * as React from "react";

/**
 * The ambient "auto-update button" for the desktop shell. electron-updater
 * downloads a new version in the BACKGROUND (desktop/updater.js); this pill is
 * how the user finds out and acts on it:
 *   • while downloading → a quiet "Updating… NN%" chip,
 *   • when ready        → a "Restart to update" button that installs + relaunches,
 *   • when the check FAILED (feed unreachable / not yet published) → a muted
 *     "Update check failed" link to the web download, so a broken auto-update is
 *     never INVISIBLE (an invisible failure is what strands a user on an old
 *     version until they re-download and end up with a duplicate app).
 * Idle / checking / up-to-date still render nothing — updates never nag on the
 * happy path.
 *
 * Lives in the desktop title bar (desktop-title-bar.tsx). Talks to the main
 * process ONLY through preload's `window.excellentUpdater` bridge, so it is a
 * no-op in a plain browser (the bridge is undefined) and on the server (no
 * window). It opts out of the title bar's drag region so it stays clickable.
 */

type UpdateState = {
  supported: boolean;
  status: "idle" | "checking" | "available" | "downloading" | "downloaded" | "not-available" | "error" | "unsupported";
  version: string | null;
  progress: number | null;
  error: string | null;
  downloadUrl: string;
};

type UpdaterBridge = {
  getState: () => Promise<UpdateState>;
  onState: (cb: (s: UpdateState) => void) => () => void;
  check: () => void;
  download: () => void;
  install: () => void;
};

function getBridge(): UpdaterBridge | null {
  if (typeof window === "undefined") return null;
  return (window as unknown as { excellentUpdater?: UpdaterBridge }).excellentUpdater ?? null;
}

const NO_DRAG = { WebkitAppRegion: "no-drag" } as React.CSSProperties;

export function DesktopUpdatePill() {
  const [state, setState] = React.useState<UpdateState | null>(null);

  React.useEffect(() => {
    const bridge = getBridge();
    if (!bridge) return;
    let alive = true;
    bridge
      .getState()
      .then((s) => {
        if (alive) setState(s);
      })
      .catch(() => {});
    const off = bridge.onState((s) => setState(s));
    return () => {
      alive = false;
      off?.();
    };
  }, []);

  const bridge = getBridge();
  if (!bridge || !state || !state.supported) return null;

  if (state.status === "downloading") {
    const pct = state.progress ?? 0;
    return (
      <div
        style={NO_DRAG}
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/50 px-2.5 py-0.5 type-subtext font-medium text-muted-foreground"
        title={`Downloading update${state.version ? ` ${state.version}` : ""}… ${pct}%`}
      >
        <Spinner />
        <span>Updating… {pct}%</span>
      </div>
    );
  }

  if (state.status === "downloaded") {
    return (
      <button
        type="button"
        style={NO_DRAG}
        onClick={() => bridge.install()}
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/15 px-2.5 py-0.5 type-subtext font-medium text-primary transition-colors hover:bg-primary/25"
        title={
          state.version
            ? `Excellent ${state.version} is ready — restart to update`
            : "An update is ready — restart to update"
        }
      >
        <UpdateGlyph />
        <span>Restart to update</span>
      </button>
    );
  }

  // The feed failed (unreachable / not yet published / checksum reject). Surface
  // it QUIETLY but visibly and route to the same web download the menu + recovery
  // screen use — never leave a stalled update silent. Links open in the browser
  // via main.js's window-open handler (target=_blank → openExternallyIfSafe).
  if (state.status === "error") {
    return (
      <a
        style={NO_DRAG}
        href={state.downloadUrl}
        target="_blank"
        rel="noreferrer"
        className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 type-subtext font-medium text-muted-foreground transition-colors hover:text-foreground"
        title={`Couldn’t check for updates — download the latest from ${state.downloadUrl}`}
      >
        <span aria-hidden>↓</span>
        <span>Update check failed</span>
      </a>
    );
  }

  return null;
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent opacity-70"
    />
  );
}

function UpdateGlyph() {
  // The restart/refresh glyph — matches the recovery screen's "Restart to update".
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}
